// Alinhamento Postural Sagital Estimado — estimativa geométrica 2D na vista lateral.
// Não mede carga, centro de gravidade nem centro de pressão. Categorias indicativas, não validadas clinicamente.

// Coordenadas normalizadas ao contentor 3:4 do LandmarkCanvas → converte x para unidades de altura.
const ASPECT = 3 / 4;
// Relações clássicas (fração da altura orelha–tornozelo; + = anterior à linha de referência).
const LINE_ANTERIOR_TO_ANKLE = 0.01;
const EXPECTED = { ear: 0, shoulder: 0, hip: 0.01, knee: -0.01 };
const LABEL = { ear: "Cabeça (ouvido)", shoulder: "Ombro (acrómio)", hip: "Anca (grande trocânter)", knee: "Joelho" };
const SHORT = { ear: ["A cabeça", "a"], shoulder: ["O ombro", "o"], hip: ["A anca", "a"], knee: ["O joelho", "o"] };
const INFER = {
  ear: ["projeção anterior da cabeça", "posição posterior da cabeça"],
  shoulder: ["anteriorização do ombro / tronco superior", "posteriorização do ombro / tronco superior"],
  hip: ["translação anterior da bacia", "posteriorização da bacia"],
  knee: ["tendência para flexão do joelho em apoio", "tendência para hiperextensão do joelho"],
};
export const SAGITTAL_LIMITATION = "Estimativa geométrica do alinhamento postural, sujeita à qualidade da fotografia, ao posicionamento do paciente e à precisão dos pontos anatómicos. Não mede carga, centro de gravidade, centro de pressão nem equilíbrio dinâmico.";

const category = (v, cuts) => (v < cuts[0] ? "próximo da referência" : v < cuts[1] ? "ligeiro" : v < cuts[2] ? "moderado" : "acentuado");

function orientation(lm, override) {
  if (override === "right" || override === "left") return { facing: override === "right" ? 1 : -1, source: "confirmada pelo profissional", confident: true };
  const { ear, shoulder, ankle, foot } = lm;
  const footVote = foot && ankle && Math.abs(foot.x - ankle.x) > 0.01 ? Math.sign(foot.x - ankle.x) : 0;
  const earVote = ear && shoulder && Math.abs(ear.x - shoulder.x) > 0.01 ? Math.sign(ear.x - shoulder.x) : 0;
  if (footVote) return { facing: footVote, source: "ponto do pé", confident: !earVote || earVote === footVote };
  if (earVote) return { facing: earVote, source: "orelha / ombro (auxiliar)", confident: false };
  return { facing: 0, source: null, confident: false };
}

export function computeSagittal(lm, override) {
  const keys = ["ear", "shoulder", "hip", "knee", "ankle"];
  if (!lm || !keys.every((k) => lm[k])) return { available: false, reason: "Faltam pontos da vista lateral (orelha, ombro, anca, joelho e tornozelo)." };
  const H = lm.ankle.y - lm.ear.y;
  if (H < 0.3) return { available: false, reason: "Corpo inteiro não visível na fotografia lateral." };
  const o = orientation(lm, override);
  if (!o.facing) return { available: false, needsOrientation: true, reason: "Não foi possível determinar a orientação do paciente." };
  const xh = (p) => p.x * ASPECT;
  const ref = xh(lm.ankle) + o.facing * LINE_ANTERIOR_TO_ANKLE * H;
  const points = Object.keys(EXPECTED).map((k) => {
    const rel = ((xh(lm[k]) - ref) * o.facing) / H;            // + anterior
    const dev = (rel - EXPECTED[k]) * 100;                      // % da altura orelha–tornozelo
    const cat = category(Math.abs(dev), [2, 4, 7]);
    const dir = cat === "próximo da referência" ? null : dev > 0 ? "anterior" : "posterior";
    const inference = dir ? `Padrão compatível com ${INFER[k][dir === "anterior" ? 0 : 1]}` : "Sem desvio relevante face à relação clássica";
    const [noun, g] = SHORT[k];
    const patient = dir ? `${noun} parece ${cat === "ligeiro" ? "ligeiramente " : ""}${dir === "anterior" ? "à frente" : "atrás"} da linha de referência.`
      : `${noun} está ${g === "a" ? "próxima" : "próximo"} da linha de referência.`;
    return { key: k, label: LABEL[k], x: lm[k].x, y: lm[k].y, dev: Math.round(dev * 10) / 10, category: cat, direction: dir, inference, patient };
  });
  const deg = (Math.atan2((xh(lm.ear) - xh(lm.ankle)) * o.facing, H) * 180) / Math.PI;
  const gcat = category(Math.abs(deg), [1.5, 3, 5]);
  const gdir = gcat === "próximo da referência" ? null : deg > 0 ? "anterior" : "posterior";
  return {
    available: true, orientation: o, refX: ref / ASPECT, points,
    global: { deg: Math.round(deg * 10) / 10, category: gcat, direction: gdir,
      inference: gdir ? `Inclinação global ${gdir} estimada do corpo sobre os tornozelos (${gcat})` : "Corpo globalmente próximo da vertical sobre os tornozelos",
      patient: gdir ? `No conjunto, o corpo parece inclinar-se ${gcat === "ligeiro" ? "ligeiramente " : ""}para ${gdir === "anterior" ? "a frente" : "trás"}.` : "No conjunto, o corpo está próximo da vertical." },
  };
}
